import gql from 'graphql-tag';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useMemo} from 'react';
import useAccountId from '../../hooks/useAccountId';
import {
  GetClientFilesV1,
  GetClientFilesV1Variables,
} from '../../schema-types/GetClientFilesV1';
import {
  UploadClientDocumentV1,
  UploadClientDocumentV1Variables,
} from '../../schema-types/UploadClientDocumentV1';
import {
  ClientDocumentDownloadV1,
  ClientDocumentDownloadV1Variables,
} from '../../schema-types/ClientDocumentDownloadV1';
import validateUUID from '../../utils/validateUUID';

export const GET_CLIENT_FILES_V1 = gql`
  query GetClientFilesV1($accountId: UUID!) {
    clientFiles(accountId: $accountId) {
      documentId
      name
      url
      createdAt
      fileType
      publishPortal
      documentType
    }
  }
`;

export const DOWNLOAD_CLIENT_FILE_V1 = gql`
  query ClientDocumentDownloadV1($accountCuroId: UUID!, $documentId: String!) {
    clientDocumentDownload(accountCuroId: $accountCuroId, documentId: $documentId) {
      ok
      content
      contentType
      errors
    }
  }
`;

export const UPLOAD_CLIENT_FILE_V1 = gql`
  mutation UploadClientDocumentV1(
    $accountId: UUID!
    $documentType: String
    $fileData: String!
    $fileName: String!
  ) {
    clientDocumentUpload(
      accountId: $accountId
      documentType: $documentType
      fileData: $fileData
      fileName: $fileName
    ) {
      ok
      errors
    }
  }
`;

export const useClientFilesV1 = () => {
  const {accountId} = useAccountId();
  const {
    data,
    error: clientFilesError,
    loading: clientFilesLoading,
  } = useQuery<GetClientFilesV1, GetClientFilesV1Variables>(GET_CLIENT_FILES_V1, {
    variables: {
      accountId: validateUUID(accountId)!,
    },
    skip: !accountId,
  });

  const clientFiles = useMemo(() => {
    return (
      data?.clientFiles?.map((f) => {
        return {
          id: f.documentId,
          ...f,
        };
      }) || []
    );
  }, [data]);

  const clientFilesPDFOnly = useMemo(() => {
    return (
      data?.clientFiles
        ?.filter((f) => f.fileType === 'application/pdf' && f.publishPortal)
        .map((f) => {
          return {
            ...f,
            id: f.name,
            // removing the file extension from the name
            name: f.name?.replaceAll('.pdf', '').replaceAll('.PDF', ''),
          };
        }) || []
    );
  }, [data]);

  const [uploadClientFile, {loading: uploadClientFileLoading}] = useMutation<
    UploadClientDocumentV1,
    UploadClientDocumentV1Variables
  >(UPLOAD_CLIENT_FILE_V1, {
    refetchQueries: [
      {
        query: GET_CLIENT_FILES_V1,
        variables: {
          accountId,
        },
      },
    ],
  });

  const [
    downloadClientFile,
    {loading: downloadClientFileLoading, data: downloadClientFileData},
  ] = useLazyQuery<ClientDocumentDownloadV1, ClientDocumentDownloadV1Variables>(
    DOWNLOAD_CLIENT_FILE_V1
  );

  return {
    clientFiles,
    clientFilesError,
    clientFilesLoading,
    clientFilesPDFOnly,
    uploadClientFile,
    uploadClientFileLoading,
    downloadClientFile,
    downloadClientFileLoading,
    downloadClientFileData,
  };
};
